import { EditorContent } from "@tiptap/react";
import { ComponentProps, useContext, useEffect, useState } from "react";

import styles from "@components/fields/RichTextareaEditor.module.css";
import RichTextareaMenu from "@components/fields/RichTextareaMenu";
import { RichTextareaContext } from "@components/fields/RichTextareaProvider";

type Props = Pick<
  ComponentProps<typeof RichTextareaMenu>,
  "actions" | "controls" | "disabled" | "showArticleLinksEvenWithoutJourneyId" | "submitDisabled" | "submitVariant"
> &
  Pick<ComponentProps<typeof EditorContent>, "id"> & {
    extraStyles?: string;
    hasError?: boolean;
    size?: "small" | "default" | "large" | "xlarge";
    value?: string;
  };

const RichTextareaEditor = ({
  disabled = false,
  extraStyles,
  hasError = false,
  id,
  showArticleLinksEvenWithoutJourneyId = false,
  size = "default",
  value,
  ...richTextAreaMenuProps
}: Props) => {
  const [needsContentRefresh, setNeedsContentRefresh] = useState<boolean>(false);
  const { editor, editorRefresher } = useContext(RichTextareaContext);

  useEffect(() => {
    if (value !== undefined && value !== editor?.getHTML()) {
      if (editor) {
        editor.commands.setContent(value);
      } else {
        setNeedsContentRefresh(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorRefresher?.value]);

  useEffect(() => {
    if (value !== undefined && editor !== null && needsContentRefresh) {
      editor.commands.setContent(value);
      setNeedsContentRefresh(false);
    }

    if (value === undefined && needsContentRefresh) {
      setNeedsContentRefresh(false);
    }
  }, [editor, needsContentRefresh, value]);

  useEffect(() => {
    if (value === "" && value !== editor?.getHTML()) {
      editor?.commands.setContent(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const editorClassNames = [styles.editor];
  if (hasError) editorClassNames.push(styles.hasError);
  if (extraStyles) editorClassNames.push(extraStyles);

  const editorContentClassNames = [styles.editorContent, styles[size]];

  return (
    <>
      {editor && (
        <div className={editorClassNames.join(" ")}>
          <div
            className={editorContentClassNames.join(" ")}
            onScroll={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <EditorContent
              disabled={disabled}
              editor={editor}
              id={id}
              // the focus handling when you click on the wrapper content moves the cursor to the
              // end position, but we don't want to do that if you're clicking on the text in the
              // editable area
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                editor.commands.focus();
              }}
            />
          </div>
          {richTextAreaMenuProps.controls.length > 0 && (
            <RichTextareaMenu
              disabled={disabled}
              showArticleLinksEvenWithoutJourneyId={showArticleLinksEvenWithoutJourneyId}
              {...richTextAreaMenuProps}
            />
          )}
        </div>
      )}
    </>
  );
};

export default RichTextareaEditor;
